'use client';

import { useEffect } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';

/**
 * SPA Page View Tracker for Next.js
 * 
 * This component tracks client-side navigation (SPA route changes) and pushes
 * virtual page view events to the dataLayer for Google Tag Manager.
 * 
 * Why this is needed:
 * - Next.js uses client-side routing (no full page reloads)
 * - GTM's default "Page View" trigger only fires on initial page load
 * - Without this, only the landing page is tracked, missing all subsequent navigation
 * 
 * How it works:
 * - usePathname() detects route changes
 * - useSearchParams() detects query string changes
 * - Pushes a 'virtual_pageview' event to dataLayer on each change
 * - GTM can then fire GA4 tags based on this event
 */
export default function SPAPageViewTracker() {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  useEffect(() => {
    // Skip tracking on initial mount - GTM handles the first page load
    // We only want to track subsequent client-side navigations
    if (typeof window === 'undefined') return;

    // Construct the full URL path with search params
    const url = pathname + (searchParams?.toString() ? `?${searchParams.toString()}` : '');

    // Small delay to ensure the page has finished transitioning
    // This also helps batch rapid navigation events
    const timer = setTimeout(() => {
      // Push virtual page view to dataLayer
      if (window.dataLayer) {
        window.dataLayer.push({
          event: 'virtual_pageview',
          page_path: url,
          page_location: window.location.href,
          page_title: document.title
        });

        // Debug logging (remove in production or gate behind env var)
        if (process.env.NODE_ENV === 'development') {
          console.log('[SPA Tracker] Virtual pageview:', url);
        }
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [pathname, searchParams]);

  return null;
}
